import React from 'react';
import { useQuery } from '@apollo/client/react';
import { Link } from 'react-router';

import { Table } from '@repo/ui/Table';
import { ButtonLink } from '@repo/ui/Button';
import { PAGE_SCHEMA, Pagination, decodePaging } from '@repo/ui/Pagination';
import { useZodQuery } from '@repo/hooks/useZodQuery';
import { useUser } from '@repo/hooks/useUser';
import { coerce } from '@repo/util/coerceForTable';
import type { Mercury } from '@repo/types/Models';

import { POLICIES } from '@/lib/Authorization';
import { DID_RESOURCES, type DidResource } from '../lib/didsForResource';

const PAGE_SIZE = 10;

type DidRow = Pick<
  Mercury.Models.DID,
  'id' | 'insertedAt' | 'number' | 'type' | 'smsEnabled' | 'mmsEnabled'
>;

interface DidsForResourceTableProps {
  resource: DidResource;
  resourceId: string;
  accountId: string;
}

/**
 * Every number attached to an API key or switch. Each number opens its own page;
 * "Update" jumps straight to the form that changes this attachment.
 */
const DidsForResourceTable = ({ resource, resourceId, accountId }: DidsForResourceTableProps) => {
  const user = useUser();
  const { query, connection, moveAnchor } = DID_RESOURCES[resource];

  const filter = useZodQuery({ schema: PAGE_SCHEMA, ignoreList: ['page'], coerce });

  const { loading, error, data } = useQuery(query, {
    variables: { ...decodePaging(filter.queryValues.page, PAGE_SIZE), id: resourceId },
    notifyOnNetworkStatusChange: true,
  });

  if (error) console.error(error);

  const result = (
    data as
      | Record<
          string,
          Mercury.GQL.Connection<DidRow> & {
            pageInfo: Mercury.GQL.PageInfo;
          }
        >
      | undefined
  )?.[connection];
  const rows = result?.edges.map(({ node }) => node) ?? [];

  const canShow = POLICIES['dids:show'](user);
  const canMove = POLICIES['dids:update'](user);
  const didPath = (id: string) => `/a/${accountId}/numbers/${id}`;

  return (
    <div>
      <Table
        loading={loading}
        pageSize={PAGE_SIZE}
        data={rows}
        dataType="numbers"
        error={!!error}
        columns={[
          {
            title: 'Number',
            Cell({ entry: { id, number } }) {
              return canShow ? (
                <Link to={didPath(id)} className="underline">
                  {number}
                </Link>
              ) : (
                <span>{number}</span>
              );
            },
          },
          {
            title: 'Type',
            Cell({ entry: { type } }) {
              return <p>{type === 'TENDLC' ? '10DLC' : type}</p>;
            },
          },
          {
            title: 'Capabilities',
            Cell({ entry: { smsEnabled, mmsEnabled } }) {
              return <p>{[smsEnabled && 'SMS', mmsEnabled && 'MMS'].filter(Boolean).join(', ')}</p>;
            },
          },
          {
            title: canMove ? 'Actions' : '',
            width: canMove ? 'sm' : 'hidden',
            Cell({ entry: { id } }) {
              return canMove ? (
                <ButtonLink
                  as={Link}
                  size="sm"
                  variant="secondary"
                  href={`${didPath(id)}/update#${moveAnchor}`}
                >
                  Update
                </ButtonLink>
              ) : (
                <></>
              );
            },
          },
        ]}
      />
      {!error ? <Pagination pageInfo={result?.pageInfo} filter={filter} /> : null}
    </div>
  );
};

export default DidsForResourceTable;
